import React, { useContext, useState } from 'react'
import { Button, ListMenu, ListMenuButton, Modal, NotificationManagerContext, Popover,
   Spacer, TabMenu, TabMenuButton, Text, View } from 'oio-react'
import { Link, Route, Switch, useHistory, useParams, useRouteMatch } from 'react-router-dom'
import { settingsContentUrl } from 'config/constants/urls'
import PageDeleteConfirm from 'src/apps/Page/components/DeleteConfirm'
import PageCreate from 'src/apps/Page/pages/Create'
import ContentListSearchBox from 'src/components/Settings/ContentListSearchBox'
import ContentListItemRow from 'src/components/Settings/ContentListItemRow'
import ProtoHeadingBlock from 'src/components/Settings/ProtoHeadingBlock'
import ProtoReturnBar from 'src/components/Settings/ProtoReturnBar'
import { usePageList, useUpdatePage } from 'src/graphql/hooks'
import ThemeContext from 'src/libraries/contexts/Theme'
import PageSettings from './Settings'
import PageVisualEditor from './VisualEditor'

const SettingsContentPages = () => {
   const history = useHistory()
   const { pageStatus } = useParams()
   const match = useRouteMatch()

   const theme = useContext(ThemeContext)
   const { showNotification } = useContext(NotificationManagerContext)
   const [modalIsOpen, setModalIsOpen] = useState(true)
   const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false)
   const [searchValue, setSearchValue] = useState('')
   const [activeSearchValue, setActiveSearchValue] = useState('')

   const [popoverConfig, setPopoverConfig] = useState({
      anchorElement: null,
      focusedValue: null,
      isOpen: false
   })

   const pageListFilter = {
      archived: 'exclude',
      search: activeSearchValue,
      unpublished: 'exclude',
      limit: 25
   }

   if (pageStatus === 'archived') {
      pageListFilter.archived = 'only'
      pageListFilter.unpublished = 'include'
   } else if (pageStatus === 'unpublished') {
      pageListFilter.unpublished = 'only'
   }

   const { pageList, loading, refetch, fetchMore } = usePageList(pageListFilter)
   const { updatePage } = useUpdatePage()

   const handleUpdatePage = async (values) => {
      try {
         await updatePage({ id: popoverConfig.focusedValue.id }, values)
         showNotification({
            message: 'Changes saved successfully',
            title: 'Success!',
            type: 'success'
         })
         refetch()
      } catch (err) {
         showNotification({
            message: 'An error occurred while saving changes',
            title: 'Error',
            type: 'error'
         })
      }
   }

   const handleLoadMore = () => {
      fetchMore({
         variables: {
            start: pageList.listInfo.nextCursor
         },
         updateQuery: (prevResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) {
               return prevResult
            }

            return {
               ...prevResult,
               pageList: {
                  ...prevResult.pageList,
                  items: [
                     ...prevResult.pageList.items,
                     ...fetchMoreResult.pageList.items
                  ],
                  listInfo: fetchMoreResult.pageList.listInfo
               }
            }
         }
      })
   }

   const handleClosePopover = () => {
      setPopoverConfig(config => ({
         ...config,
         isOpen: false,
         anchorElement: null
      }))
   }

   return (
      <View>
         <ProtoReturnBar
            display="block[a-d] none[e-f]"
            returnLinkName="Content & Pages"
            returnLinkTo={settingsContentUrl}
         />
         <View
            display="flex"
            justifyContent="center">
            <View
               width="100%"
               maxWidth="100%[a-c] 600px[d-f]"
               padding="40px 20px">
               <View
                  width="100%"
                  display="flex"
                  flexFlow="column[a-c] row[d-f]">
                  <View paddingRight="30px">
                     <ProtoHeadingBlock
                        title="Custom Pages"
                        description="This is a list of all the content pages for your Community Site. Only published Content Pages will appear to users that are not admins."
                     />
                  </View>
                  <View flex="0 0 auto" paddingTop="30px">
                     <Link to={`${settingsContentUrl}/pages/unpublished/create`}>
                        <Button
                           width="120px"
                           name="Create"
                           color={theme.protoSettingsHighlightBackgroundColor}
                           textColor={theme.protoSettingsHighlightForegroundColor}
                           textWeight="medium"
                           size="md"
                           textSize="0.9"
                           padding="25px"
                        />
                     </Link>
                  </View>
               </View>
               <Spacer size="3" />
               <TabMenu
                  backgroundColor="#F7F7F4"
                  buttonPaddingHorizontal="0px"
                  buttonSpacing="25px">
                  <TabMenuButton
                     name="Published"
                     linkTo="/settings/content/pages/published"
                     exact
                  />
                  <TabMenuButton
                     name="Unpublished"
                     linkTo="/settings/content/pages/unpublished"
                  />
                  <TabMenuButton
                     name="Archived"
                     linkTo="/settings/content/pages/archived"
                  />
               </TabMenu>
               <ContentListSearchBox
                  value={searchValue}
                  onChange={setSearchValue}
                  onSubmit={setActiveSearchValue}
               />
               <View
                  width="100%"
                  borderRadius="3px"
                  backgroundColor="#fff">
                  <View width="100%">
                     {pageList.items.length === 0 && (
                        <View
                           float="left"
                           textAlign="center"
                           backgroundColor="#fff"
                           width="100%"
                           padding="30px">
                           <Text size="1.5">
                              No custom pages to show here
                           </Text>
                        </View>
                     )}
                     {pageList.items.map((page, index) => (
                        <ContentListItemRow
                           key={page.id}
                           dateLastUpdated={page.dateLastUpdated}
                           editUrl={`${match.url}/${page.slug}/edit`}
                           name={page.title}
                           onOptionsButtonClick={(e) => {
                              setPopoverConfig({
                                 isOpen: true,
                                 anchorElement: e.currentTarget,
                                 focusedValue: page
                              })
                           }}
                        />
                     ))}
                     {pageList.listInfo.hasNext && (
                        <View
                           float="left"
                           width="100%"
                           display="flex"
                           justifyContent="center"
                           padding="12px 0px">
                           <Button
                              color="#000"
                              textColor="#fff"
                              size="xs"
                              onClick={handleLoadMore}
                              name="Load More"
                              mode={loading ? 'loading' : 'normal'}
                              rounded
                           />
                        </View>
                     )}
                     {/* Page List Item Popover */}
                     <Popover
                        anchorElement={popoverConfig.anchorElement}
                        anchorOriginHorizontal="right"
                        anchorOriginVertical="top"
                        backgroundColor="#fff"
                        borderRadius="6px"
                        width="180px"
                        onBodyClick={handleClosePopover}
                        open={popoverConfig.isOpen}>
                        <View
                           float="left"
                           width="100%"
                           backgroundColor="#fff"
                           borderRadius="6px"
                           boxShadow="3px 3px 12px rgba(0,0,0,0.2)">
                           {popoverConfig.focusedValue && (
                              <>
                                 <View float="left" width="100%">
                                    <ListMenu
                                       buttonSize="md"
                                       buttonTextColor="#333"
                                       dividerLineStyle="1px solid #e5e5e5">
                                       <ListMenuButton
                                          name="Open in Visual Editor"
                                          linkTo={`${match.url}/${popoverConfig.focusedValue.slug}/edit`}
                                          isActive={false}
                                       />
                                       <ListMenuButton
                                          name="Page Info & Settings"
                                          linkTo={`${match.url}/${popoverConfig.focusedValue.slug}/settings`}
                                          isActive={false}
                                       />
                                    </ListMenu>
                                 </View>
                                 <View float="left" width="100%" borderTop="1px solid #e5e5e5">
                                    <ListMenu
                                       buttonSize="sm"
                                       dividerLineStyle="none">
                                       <ListMenuButton
                                          onClick={() => handleUpdatePage({
                                             published: pageStatus !== 'published',
                                             archived: false
                                          })}
                                          name={pageStatus !== 'published'
                                             ? 'Publish'
                                             : 'Unpublish'
                                          }
                                       />
                                       {pageStatus === 'archived' && (
                                          <ListMenuButton
                                             onClick={() => handleUpdatePage({
                                                published: false,
                                                archived: false
                                             })}
                                             name="Set as Unpublished"
                                          />
                                       )}
                                       {pageStatus !== 'archived' && (
                                          <ListMenuButton
                                             onClick={() => handleUpdatePage({
                                                archived: true
                                             })}
                                             name="Archive"
                                          />
                                       )}
                                       <ListMenuButton
                                          onClick={() => setDeleteModalIsOpen(true)}
                                          name="Delete"
                                       />
                                    </ListMenu>
                                 </View>
                              </>
                           )}
                        </View>
                     </Popover>
                  </View>
               </View>
            </View>
         </View>
         <Switch>
            <Route
               path={`${match.url}/create`}
               render={() => (
                  <Modal
                     borderRadius="6px"
                     width="300px"
                     onCloseTrigger={() => setModalIsOpen(false)}
                     onCloseComplete={() => {
                        history.push(match.url)
                        setModalIsOpen(true)
                     }}
                     open={modalIsOpen}>
                     <PageCreate
                        onCancelButtonClick={() => setModalIsOpen(false)}
                        onCreate={(page) => {
                           setModalIsOpen(true)
                           history.push(`${match.url}/${page.slug}/edit`)
                           setSearchValue('')
                           setActiveSearchValue('')
                           refetch()
                        }}
                        targetType="organization"
                     />
                  </Modal>
               )}
            />
            <Route
               path={`${match.path}/:pageSlug/edit`}
               render={props => (
                  <Modal
                     width="100%"
                     height="100%"
                     openAnimationName="fadeIn"
                     open={modalIsOpen}>
                     <PageVisualEditor returnUrl={match.url} />
                  </Modal>
               )}
            />
            <Route
               path={`${match.path}/:pageSlug/settings`}
               render={() => (
                  <Modal
                     borderRadius="6px"
                     width="100%[a-d] 1200px[e-f]"
                     height="100%[a-d] 85%[e-f]"
                     onCloseComplete={() => {
                        history.push(match.url)
                        setModalIsOpen(true)
                     }}
                     onCloseTrigger={() => setModalIsOpen(false)}
                     open={modalIsOpen}>
                     <PageSettings returnUrl={match.url} />
                  </Modal>
               )}
            />
         </Switch>
         {!!popoverConfig.focusedValue && (
            <Modal
               borderRadius="6px"
               width="90%[a-b] 300px[c-f]"
               onCloseTrigger={() => setDeleteModalIsOpen(false)}
               open={deleteModalIsOpen}>
               <PageDeleteConfirm
                  pageId={popoverConfig.focusedValue.id}
                  pageTitle={popoverConfig.focusedValue.title}
                  onCancelButtonClick={() => setDeleteModalIsOpen(false)}
                  onDelete={() => {
                     setDeleteModalIsOpen(false)
                     refetch()
                  }}
               />
            </Modal>
         )}
      </View>
   )
}

export default SettingsContentPages
